import { useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { MileageRateConfigurationFragment } from 'generated/resources/graphql'

import { NumberInput, Select } from '@factorialco/f0-react/dist/experimental'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import { castError } from 'modules/expenses/components/expenses/Detail/_newForm/castError'
import withCustomizationFields, {
  WithCustomizationFieldsProps,
} from 'modules/expenses/components/expenses/Detail/_newForm/withCustomizationFields'
import withFieldPermissions, {
  WithFieldPermissionsProps,
} from 'modules/expenses/components/expenses/Detail/_newForm/withFieldPermissions'
import { getLabelFromRate } from 'modules/expenses/lib/expensables/expensable'

const FIELD_NAME = 'rate'

interface Props extends WithFieldPermissionsProps, WithCustomizationFieldsProps {
  rates?: MileageRateConfigurationFragment[]
}

const RateSelectorInput = ({ rates, isFieldReadOnly, isFieldMandatory }: Props) => {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext()

  const currency = watch('currency')
  const rate = watch('rate')

  const {
    access: { locale },
  } = useCurrentSession()

  const initialRate = rates?.find((r) => r.rate === rate)
  const [selectedRate, setSelectedRate] = useState<MileageRateConfigurationFragment | null>(
    initialRate ?? null
  )

  const rateForSelect = rates?.map((rateItem) => {
    let label: string
    try {
      label = getLabelFromRate(locale, currency, Number(rateItem.rate), rateItem.name)
    } catch {
      // getLabelFromRate uses Intl.NumberFormat which throws on invalid currency codes
      label = `${Number(rateItem.rate)} - ${rateItem.name}`
    }
    return {
      label,
      value: String(rateItem.id),
    }
  })

  const getRate = (rateId: string) => {
    return rates?.find((mileageRate) => mileageRate.id === rateId)
  }

  return (
    <Controller
      name={FIELD_NAME}
      control={control}
      render={() => (
        <Select
          label={i18n.t('expenses_v2.forms.fields.rate.label')}
          placeholder={i18n.t('expenses_v2.forms.new_form.fields.rate.placeholder')}
          options={rateForSelect ?? []}
          value={selectedRate?.id?.toString() ?? ''}
          onChange={(value: string) => {
            const rateValue = getRate(value)
            setSelectedRate(rateValue ?? null)
            setValue('rate', rateValue?.rate, {
              shouldValidate: true,
              shouldDirty: true,
            })
          }}
          error={castError(errors?.rate)}
          disabled={isFieldReadOnly}
          required={isFieldMandatory}
        />
      )}
    />
  )
}

const RateInput = ({ isFieldMandatory, isFieldReadOnly }: Props) => {
  const {
    control,
    formState: { errors },
  } = useFormContext()

  return (
    <Controller
      name={FIELD_NAME}
      control={control}
      render={({ field }) => (
        <NumberInput
          label={i18n.t('expenses_v2.forms.fields.rate.label')}
          placeholder="0.0"
          value={field.value}
          maxDecimals={5}
          required={isFieldMandatory ?? false}
          disabled={isFieldReadOnly}
          locale={i18n.locale}
          onChange={(value) => {
            field.onChange(value)
          }}
          error={castError(errors?.rate)}
        />
      )}
    />
  )
}

const RateField = (props: Props) => {
  const { rates, isFieldReadOnly, isFieldMandatory } = props

  if (rates?.length === 0) {
    return <RateInput {...props} />
  } else {
    return (
      <RateSelectorInput
        {...props}
        rates={rates || []}
        isFieldReadOnly={isFieldReadOnly}
        isFieldMandatory={isFieldMandatory}
      />
    )
  }
}

export default withCustomizationFields(withFieldPermissions(RateField, 'rate'))
